import { DesignIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/config/DesignIcon";
import { DomainIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/config/DomainIcon";
import { MenusIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/config/MenusIcon";
import { PermissionIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/config/PermissionIcon";
import { SEOIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/config/SEOIcon";
import { BillboardIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/pages/BillboardIcon";
import { TagsIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/pages/TagsIcon";
import { SalesIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/SalesIcon";
import { IOptionsHook, ISubSection } from "pages/[tenant]/console/types";

import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useFeature } from "shared/utils/hooks/useFeature";
import { usePlanFeature } from "shared/utils/hooks/usePlanFeature";

import { CommunityIcon } from "@Console/assets/icons/CommunityIcon";
import { AuthenticationIcon } from "@Console/assets/icons/sidebarSection/config/AuthenticationIcon";
import ConsoleSentHistoryIcon from "@Console/assets/icons/sidebarSection/config/ConsoleSentHistoryIcon";
import ConsoleTemplateIcon from "@Console/assets/icons/sidebarSection/config/ConsoleTemplateIcon";
import { OrganizationsIcon } from "@Console/assets/icons/sidebarSection/sales/OrganizationsIcon";
import {
  permissionsAuth,
  permissionsConfigBillboard,
  permissionsConfigDesign,
  permissionsConfigDomain,
  permissionsConfigPlatform,
  permissionsConfigSales,
  permissionsItemsMenu,
} from "@Console/utils/permissionsAreasAdminConsole";

export const useConfigsOptions = (): IOptionsHook => {
  const isCustomDomainFeatureEnabled = usePlanFeature("f_website_front_domain");
  const isOrganizationsFeatureEnabled = usePlanFeature("f_orgs");
  const isPermissionEnabled = useFeature("f_console_permissions");
  const isEmailTemplateEnabled = useFeature("f_mail_templates");

  const { loggedUser, rolesUser } = useAuthContext();

  const isAdmin =
    loggedUser?.admin ||
    loggedUser?.superadmin ||
    loggedUser?.console_role?.slug === "superadmin" ||
    loggedUser?.console_role?.slug === "admin";

  const hasPermission = (permissionsList: string[]): boolean =>
    rolesUser?.some((permission) => permissionsList?.includes(permission));

  const shouldHideOption = (permissionsList?: string[], condition = true) =>
    !condition || (permissionsList && !hasPermission(permissionsList));

  const isEmailNotificationsEnabled = useFeature("f_mail_templates");
  const isInternalNotificationsEnabled = useFeature("f_notification_templates");

  const shouldShowTemplateSideBarButton =
    isEmailNotificationsEnabled || isInternalNotificationsEnabled;

  const sectionLabel = "Configurações";
  const optionsSubSections: ISubSection[] = [
    {
      label: "Definições",
      options: [
        {
          id: 1,
          label: "Plataforma",
          description:
            "Defina as configurações gerais da sua plataforma e como ela deve ser apresentada ao público.",
          path: "/console/platform",
          icon: <TagsIcon />,
          shouldHide: shouldHideOption(permissionsConfigPlatform),
        },
        {
          id: 2,
          label: "Comunidade",
          path: "/console/community",
          icon: <CommunityIcon />,
        },
        {
          id: 3,
          label: "Aparência e design",
          description:
            "Personalize sua plataforma, configure sua identidade visual e reforce sua marca.",
          path: "/console/design",
          icon: <DesignIcon />,
          shouldHide: shouldHideOption(permissionsConfigDesign),
        },
        {
          id: 4,
          label: "Vendas",
          description:
            "Configure a comunicação e comportamento das vendas realizadas através da Ensinio Pay.",
          path: "/console/sales",
          icon: <SalesIcon />,
          shouldHide: shouldHideOption(permissionsConfigSales),
        },
        {
          id: 5,
          label: "Domínios",
          path: "/console/domains",
          icon: <DomainIcon />,
          shouldHide: shouldHideOption(
            permissionsConfigDomain,
            isCustomDomainFeatureEnabled,
          ),
        },
        {
          id: 6,
          label: "Billboard",
          description:
            "Configure sua vitrine e organize suas seções e banners de destaques",
          path: "/console/billboard",
          icon: <BillboardIcon />,
          shouldHide: shouldHideOption(permissionsConfigBillboard),
        },
        {
          id: 7,
          label: "Menus",
          description:
            "Defina seus menus públicos e configure a navegação da sua plataforma",
          path: "/console/menus",
          icon: <MenusIcon />,
          shouldHide: shouldHideOption(permissionsItemsMenu),
        },
        {
          id: 8,
          label: "Funções administrativas",
          path: "/console/permissions",
          icon: <PermissionIcon />,
          shouldHide: shouldHideOption(
            ["visualizar-funcoes-de-permissoes"],
            isPermissionEnabled,
          ),
        },
        {
          id: 9,
          label: "Autenticação",
          path: "/console/auth",
          icon: <AuthenticationIcon />,
          description:
            "Alterne entre dois modos de login/cadastro na sua plataforma.",
          shouldHide: shouldHideOption(permissionsAuth),
        },
        {
          id: 10,
          label: "SEO",
          path: "/console/seo",
          icon: <SEOIcon />,
          shouldHide: true,
        },
        {
          id: 11,
          label: "Organizações",
          path: "/console/organizations",
          icon: <OrganizationsIcon />,
          shouldHide: !isOrganizationsFeatureEnabled || !isAdmin,
        },
      ],
    },
    {
      label: "Notificações",
      options: [
        {
          id: 12,
          label: "Templates",
          description:
            "Aqui você conseguirá consultar todas as notificações que sua plataforma dispara.",
          path: "/console/email-templates",
          icon: <ConsoleTemplateIcon />,
          shouldHide: !shouldShowTemplateSideBarButton,
        },
        {
          id: 13,
          label: "Histórico de envios",
          description:
            "Aqui você pode consultar o histórico de todos os templates enviados para os seus usuários.",
          path: "/console/email-history",
          icon: <ConsoleSentHistoryIcon />,
          shouldHide: !isEmailTemplateEnabled,
        },
      ],
    },
  ];

  return {
    label: sectionLabel,
    subSections: optionsSubSections,
  };
};
